import { useTheme } from 'styled-components';
import Row from '@sdk/components/ui/spacing/Row';
import H3 from '@sdk/components/ui/typography/H3';
import Text16 from '@sdk/components/ui/typography/Text16';
import Text14 from '@sdk/components/ui/typography/Text14';
import Button from '@sdk/components/ui/buttons/Button';
import Text12 from '@sdk/components/ui/typography/Text12';
import Href from '@sdk/components/ui/Href';
import gettext from '@sdk/lang/gettext';
import TranslateWithComponents from '@sdk/lang/TranslateWithComponents';
import CloseIcon from '@sdk/components/ui/CloseIcon';
import StyledSvg from '@sdk/components/ui/StyledSvg';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { CloseContainer } from '@sdk/components/ui/modal/modal.styles';
import type { PremiumFeatureAccessLevel } from '@sdk/authentication/types';
import type { AuthUserType } from '@sdk/authentication/types/AuthUserType';

import CrownSvg from '@web/assets/svg/UI/crown.svg';
import { FEATURES_INFO } from '@web/premiumGuard/ModalGuard/modalGuard.consts';
import { useModalGuard } from '@web/premiumGuard/ModalGuard/modalGuard.hooks';
import { PremiumModalNames } from '@web/premiumGuard/constants/modalNames';
import {
  ButtonContainer,
  Container,
  LeftColumn,
  RightColumn,
  StyledRow,
  StyledUl,
  WrappedContent,
} from '@web/premiumGuard/ModalGuard/modalGuard.styles';
import { FeatureNames } from '@web/account/types';

type Props = {
  premiumModalName: PremiumModalNames;
  premiumFeatureName: FeatureNames;
  premiumFeatureAccessLevel: PremiumFeatureAccessLevel;
  currentUser: AuthUserType;
  goToBilling: () => void;
  onDismiss?: () => void; // Hide close button if onDismiss is absent, since container is handling it in this case
  purchasePlan: () => void;
};

/*
 * The core component logic of ModalGuard. Use ModalGuard component in react context
 * and ModalGuardAngular wrapper in angular context.
 */
const ModalGuardInner = ({
  premiumModalName,
  premiumFeatureName,
  premiumFeatureAccessLevel,
  currentUser,
  goToBilling,
  onDismiss,
  purchasePlan,
}: Props) => {
  const theme = useTheme();
  const { accessLevelName, firstEightFeatures } = useModalGuard({
    currentUser,
    premiumFeatureAccessLevel,
    premiumFeatureName,
  });

  const { isToastBilling } = currentUser.org;

  return (
    <Container>
      {!!onDismiss && (
        <CloseContainer top={20} right={20}>
          <CloseIcon spacing={10} onClick={(): void => onDismiss?.()} />
        </CloseContainer>
      )}

      <LeftColumn pLeft={55} pRight={15} justifyContent="center">
        <H3 bold pBottom={10} themeColor={ThemeColorType.primary}>
          {FEATURES_INFO[premiumModalName].header}
        </H3>
        <Row pBottom={20} alignItems="center">
          <CrownSvg />
          <Text16 pLeft={10} semiBold themeColor={ThemeColorType.primaryText}>
            {gettext('Available in %(accessLevelName)s', {
              accessLevelName,
            })}
          </Text16>
        </Row>
        <Text14 pBottom={10} semiBold themeColor={ThemeColorType.primaryText} pRight={10}>
          <TranslateWithComponents
            replaceComponents={{
              featureName: (
                <Text14 semiBold themeColor={ThemeColorType.primary}>
                  {FEATURES_INFO[premiumModalName].name}
                </Text14>
              ),
            }}>
            {FEATURES_INFO[premiumModalName].customBenefitsText
              ? FEATURES_INFO[premiumModalName].customBenefitsText
              : gettext('Benefits of using [[featureName]] on Sling:')}
          </TranslateWithComponents>
        </Text14>
        <StyledUl>
          {FEATURES_INFO[premiumModalName].descriptions.map((item) => (
            <li key={item}>
              <Text12 themeColor={ThemeColorType.gray}>{item}</Text12>
            </li>
          ))}
        </StyledUl>
        <ButtonContainer>
          {isToastBilling ? (
            <Href
              fontSize={14}
              semiBold
              themeColor={ThemeColorType.primary}
              onClick={() => goToBilling()}>
              {gettext('Learn more about Scheduling Pro >')}
            </Href>
          ) : (
            <Button onClick={purchasePlan} buttonType={ButtonType.primary}>
              {gettext('Purchase %(accessLevelName)s', { accessLevelName })}
            </Button>
          )}
        </ButtonContainer>
      </LeftColumn>

      <RightColumn pLeft={50} pRight={50} justifyContent="center">
        <Text12 bold themeColor={ThemeColorType.primaryText}>
          {gettext('Other features in %(accessLevelName)s:', { accessLevelName })}
        </Text12>
        <WrappedContent>
          {firstEightFeatures.map(({ key, title, icon }) => (
            <Row alignItems="center" key={key} mTop={20}>
              <StyledSvg svg={icon as any} color={theme.colors[ThemeColorType.iconGray]} />
              <StyledRow>
                <Text12 themeColor={ThemeColorType.iconGray}>{title}</Text12>
              </StyledRow>
            </Row>
          ))}
        </WrappedContent>
        <Text12 themeColor={ThemeColorType.gray}>
          {gettext('view')}{' '}
          <Href
            hoverTextDecoration="none"
            fontSize={12}
            themeColor={ThemeColorType.primary}
            onClick={() => goToBilling()}>
            {gettext('pricing plans')}
          </Href>
        </Text12>
      </RightColumn>
    </Container>
  );
};

export default ModalGuardInner;
